/**
 * NS Network Solver — SIMPLE for closed-loop thermodynamic cycles.
 *
 * Architecture:
 * - Only 'boundary' type junctions provide fixed pressure BCs
 * - Component junctions are FREE — pressure is solved by SIMPLE
 * - Components apply deltaP/deltaH to outlet junctions AFTER each iteration
 * - Mass flow at boundary faces is computed by the momentum equation
 * - Pumps drive flow by adding momentum source to connected faces
 *
 * Per CLAUDE.md: "state variables are properly initialized before each
 * evaluation call — stale state from previous iterations is a common
 * and subtle bug source"
 */

import type { PipeSegment, Junction, NSComponent, SIMPLEConfig, NSSolverState } from '../types';
import { DEFAULT_SIMPLE_CONFIG } from '../types';

/**
 * Maximum per-pass specific-enthalpy change a Q_rate duty may impose on its
 * throughflow (N1 carry guard). 2.5 MJ/kg spans full sensible heating plus
 * latent evaporation of water (h_fg ≈ 2.26 MJ/kg at 1 atm) — any converted
 * Δh beyond it means the flow cannot physically carry the configured duty,
 * so the duty is NOT applied (and _appliedQ_W honestly reports 0) until the
 * flow develops. Prevents startup trickle flows from railing the h clamp.
 */
export const MAX_CARRY_DH_JKG = 2.5e6;
import { savePipeState, updatePipeProperties, faceRho } from './nsPipe';
import { propsFromPH_SI } from '../core/fluidAdapter';
import { momentumPredictor } from '../solver/momentumSolver';
import { pressureCorrectionStep } from '../solver/pressureCorrection';
import { energyStep } from '../solver/energySolver';

/**
 * Run one time step of the SIMPLE solver across the entire network.
 */
export function networkSimpleStep(state: NSSolverState, dt: number): void {
  const { pipes, junctions, components, config } = state;

  // Save old states for temporal terms
  for (const pipe of pipes.values()) {
    savePipeState(pipe);
  }

  let converged = false;
  let massResidual = Infinity;
  let momentumResidual = Infinity;
  let energyResidual = Infinity;
  let prevMassResidual = Infinity;

  // Adaptive relaxation: start with configured values, adjust based on convergence
  let alpha_P = config.pressureRelaxation;
  let alpha_u = config.velocityRelaxation;
  let alpha_h = config.enthalpyRelaxation;

  for (let iter = 0; iter < config.maxOuterIterations; iter++) {
    massResidual = 0;
    energyResidual = 0;

    // --- Step 1: Enforce boundary pressures AND enthalpies at boundary junctions ---
    for (const junction of junctions.values()) {
      if (junction.type === 'boundary') {
        // Also apply NSBoundary component if one exists for this junction
        const bcComp = junction.componentId ? components.get(junction.componentId) : null;
        if (bcComp) {
          const update = bcComp.applyBoundaryConditions(junction, junction, dt);
          // N9: NSBoundary now returns PURE deltas without mutating the
          // junction (component contract). Applying delta once lands the
          // junction EXACTLY on the target (P += P_target − P = P_target);
          // the old mutate-AND-return-delta produced 2P − P_old oscillation.
          if (update.deltaP !== 0) junction.P += update.deltaP;
          if (update.deltaH !== 0) junction.h += update.deltaH;
        }

        for (const conn of junction.connections) {
          const pipe = pipes.get(conn.pipeId);
          if (pipe) {
            const cellIdx = conn.faceIndex === 0 ? 0 : pipe.nCells - 1;
            pipe.cells[cellIdx].P = junction.P;
            pipe.cells[cellIdx].h = junction.h;
          }
        }
      }
    }

    // --- Step 2: Momentum predictor + pressure correction for each pipe ---
    for (const pipe of pipes.values()) {
      const d_f = momentumPredictor(pipe, dt, alpha_u);

      // Only boundary junctions provide fixed pressure BCs
      const pLeft = getFixedPressure(pipe, 0, junctions);
      const pRight = getFixedPressure(pipe, pipe.nCells, junctions);

      const mRes = pressureCorrectionStep(pipe, d_f, alpha_P, pLeft, pRight);
      massResidual = Math.max(massResidual, isFinite(mRes) ? mRes : 0);

      // Energy equation
      const eRes = energyStep(pipe, dt, alpha_h);
      energyResidual = Math.max(energyResidual, isFinite(eRes) ? eRes : 0);

      // Update fluid properties from (P, h)
      updatePipeProperties(pipe);
    }

    // --- Step 3: Update free junctions from adjacent pipe cells ---
    //
    // INLET-STATE FIX (Fix Wave W1, probe-verified): a junction carrying a
    // component represents that component's INLET state — every component
    // receives it as `inlet` in applyBoundaryConditions, and Step 4 writes
    // the OUTLET cell as junction + delta. The previous code averaged over
    // ALL connected cells, INCLUDING the component's own written outlet cell,
    // which fed each incremental deltaP/deltaH back into the component's own
    // input. Fixed point of that feedback:
    //     J = P_up + (ṁ_down/ṁ_up)·ΔP   ⇒   outlet = P_up + 2·ΔP at balanced
    // flow, and unboundedly more at flow imbalance (probe: a 136 kPa pump
    // sat at P_up + 3.65·ΔP; a +25 kW heat duty carried 2Q downstream).
    // TARGET-style deltas (condenser P_cond: ΔP = P_t − J) are algebraically
    // immune (J + ΔP ≡ P_t), which is why the BoP networks masked this.
    //
    // Component junctions therefore average their INFLOW-side cells only.
    // Junctions whose connections are ALL outflow (the two-junction builder
    // topology's j_X_out) and plain merge/split junctions keep the previous
    // all-connection averaging (for a plain junction the mixed state IS the
    // junction state).
    for (const junction of junctions.values()) {
      if (junction.type === 'boundary') continue;

      const isComponentJunction =
        junction.componentId != null && components.has(junction.componentId);

      // Collect pressure and enthalpy from connected pipe cells.
      // Use mass-flow-weighted average for both P and h — physically consistent
      // for multi-branch junctions where flow splits should equilibrate.
      let totalMdot = 0;
      let hFlux = 0;
      let pFlux = 0;
      let fallbackP = 0;
      let nConns = 0;

      // Pass 1 for component junctions: inflow-side (upstream) cells only.
      // Pass 2 (fallback / non-component junctions): all connected cells —
      // byte-identical to the previous behaviour.
      for (const inflowOnly of isComponentJunction ? [true, false] : [false]) {
        totalMdot = 0; hFlux = 0; pFlux = 0; fallbackP = 0; nConns = 0;

        for (const conn of junction.connections) {
          if (inflowOnly && conn.direction !== 'inflow') continue;
          const pipe = pipes.get(conn.pipeId);
          if (!pipe) continue;

          const cellIdx = conn.faceIndex === 0 ? 0 : pipe.nCells - 1;
          const P_cell = pipe.cells[cellIdx].P;
          const h_cell = pipe.cells[cellIdx].h;
          const mdot = Math.abs(pipe.faces[conn.faceIndex].mdot);

          totalMdot += mdot;
          hFlux += mdot * h_cell;
          pFlux += mdot * P_cell;
          fallbackP += P_cell;
          nConns++;
        }

        // Inflow-side pass produced usable samples → done; otherwise fall
        // through to the all-connection pass (outlet-only junctions).
        if (nConns > 0) break;
      }

      // Mass-weighted pressure; fall back to arithmetic mean if no flow
      if (totalMdot > 1e-10 && isFinite(pFlux)) {
        junction.P = pFlux / totalMdot;
      } else if (nConns > 0) {
        junction.P = fallbackP / nConns;
      }
      if (totalMdot > 0 && isFinite(hFlux)) junction.h = hFlux / totalMdot;
    }

    // --- Step 4: Apply component effects to downstream junctions ---
    for (const junction of junctions.values()) {
      if (!junction.componentId) continue;
      const comp = components.get(junction.componentId);
      if (!comp) continue;

      // Propagate inlet properties to components that need them (e.g., pumps)
      if (comp.type === 'pump') {
        for (const conn of junction.connections) {
          if (conn.direction !== 'inflow') continue;
          const pipe = pipes.get(conn.pipeId);
          if (!pipe) continue;
          const cellIdx = conn.faceIndex === 0 ? 0 : pipe.nCells - 1;
          comp.params._inletRho = pipe.cells[cellIdx].rho;
          comp.params._inletMdot = Math.abs(pipe.faces[conn.faceIndex].mdot);
          break;
        }
      }

      // N2: propagate the LIVE throughflow context to the valve family AND to
      // heat exchangers so they emit a flow-dependent, flow-SIGN-aware
      // pressure drop (ΔP = −K·ρ·u·|u|/2) instead of being hydraulically
      // dishonest.
      //   _inletRho    kg/m³  — upstream boundary-cell density
      //   _throughMdot kg/s   — SIGNED flow INTO the junction from the inflow
      //                         pipe (+ = forward through the component;
      //                         face mdot is positive in the pipe's own +x, so
      //                         faceIndex 0 needs a sign flip)
      //   _flowArea    m²     — adjacent pipe cross-section (valve bore proxy)
      //   _P_up/_P_down Pa    — upstream/downstream boundary-cell pressures
      //                         (across-component ΔP for the check-valve
      //                         cracking criterion; the shared junction P
      //                         cannot express it)
      // Read-only context (mirrors the pump _inletRho/_inletMdot convention);
      // it does not alter the SIMPLE loop itself.
      //
      // WHY heatExchanger is included (C-F5 root cause, probe-verified): the
      // HX K-loss previously used a STALE flow estimate (`_currentFlow ?? 100`
      // kg/s — nothing ever updated it), producing a CONSTANT ≈ −51 kPa
      // written to its outflow cell every iteration regardless of the actual
      // flow's magnitude or direction. Around a closed cooling loop each HX
      // therefore acted as a fixed downhill pressure STAIRCASE STEP — a
      // perpetual accelerator, not a loss — and the loop flow ran away until
      // the ±50 m/s momentum clamp (≈9.8 t/s in the DN500 line: exactly the
      // audited "13.7× design flow"). With the live signed context the HX
      // loss now opposes and scales with the real flow.
      if (comp.type === 'valve' || comp.type === 'controlValve' ||
          comp.type === 'checkValve' || comp.type === 'heatExchanger') {
        let throughMdot: number | null = null;
        for (const conn of junction.connections) {
          const pipe = pipes.get(conn.pipeId);
          if (!pipe) continue;
          const cellIdx = conn.faceIndex === 0 ? 0 : pipe.nCells - 1;
          const signIntoJunction = conn.faceIndex === 0 ? -1 : 1;
          if (conn.direction === 'inflow') {
            comp.params._inletRho = pipe.cells[cellIdx].rho;
            comp.params._flowArea = pipe.area;
            comp.params._P_up = pipe.cells[cellIdx].P;
            throughMdot = signIntoJunction * pipe.faces[conn.faceIndex].mdot;
          } else {
            comp.params._P_down = pipe.cells[cellIdx].P;
          }
        }
        if (throughMdot !== null) comp.params._throughMdot = throughMdot;
      }

      // Propagate the stream-capacity context to heat exchangers so their
      // READ-ONLY effectiveness-NTU diagnostics (hx_effectiveness/NTU/Cr/Q)
      // populate in LIVE solves. This MIRRORS the pump _inletRho/_inletMdot
      // propagation above: it ONLY writes diagnostic context params; it does
      // NOT touch the SIMPLE loop, convergence, or the HX duty/outlet calc.
      //
      // For a HX modelled on one junction carrying BOTH streams, the two inflow
      // connections are the hot- and cold-side inlets. Each stream's capacity
      // rate is C = |ṁ|·cp (W/K) from the adjacent pipe boundary cell, and its
      // inlet temperature is taken from that upstream cell (K → °C). Hot is the
      // higher-inlet-T stream, cold the lower. Units: C in W/K, T in °C —
      // exactly what NSHeatExchanger.applyBoundaryConditions expects.
      //
      // If fewer than two inflow streams are resolvable for this HX topology
      // (e.g. a single-stream HX junction thermally coupled to a sibling on a
      // separate junction), we DO NOT invent the missing stream — the context
      // is left unset and the diagnostics simply stay empty (no crash).
      if (comp.type === 'heatExchanger') {
        const inletStreams: { C: number; T_in_C: number }[] = [];
        for (const conn of junction.connections) {
          if (conn.direction !== 'inflow') continue;
          const pipe = pipes.get(conn.pipeId);
          if (!pipe) continue;
          const cellIdx = conn.faceIndex === 0 ? 0 : pipe.nCells - 1;
          const cell = pipe.cells[cellIdx];
          const mdot = Math.abs(pipe.faces[conn.faceIndex].mdot);
          const cp = cell.cp; // J/(kg·K)
          // Require a resolvable, physical stream: finite positive ṁ and cp.
          if (!(mdot > 0) || !isFinite(mdot) || !(cp > 0) || !isFinite(cp)) continue;
          if (!isFinite(cell.T)) continue;
          inletStreams.push({ C: mdot * cp, T_in_C: cell.T - 273.15 });
        }

        if (inletStreams.length >= 2) {
          // Hot = highest inlet temperature; cold = lowest. (For exactly two
          // streams this is the unambiguous hot/cold assignment.)
          let hot = inletStreams[0];
          let cold = inletStreams[0];
          for (const s of inletStreams) {
            if (s.T_in_C > hot.T_in_C) hot = s;
            if (s.T_in_C < cold.T_in_C) cold = s;
          }
          comp.params._C_hot = hot.C;
          comp.params._C_cold = cold.C;
          comp.params._T_hot_in = hot.T_in_C;
          comp.params._T_cold_in = cold.T_in_C;
        }
        // else: not resolvable for this topology — leave context unset.
      }

      const update = comp.applyBoundaryConditions(junction, junction, dt);

      // --- N1: consume the energySource channel for HEAT-SOURCE components ---
      // Q_rate-mode components (boiler / heatExchanger / custom) express their
      // duty as a TOTAL heat rate (W) on `energySource` with deltaH = 0. That
      // channel was previously dead — absorber (−60 MW), generator (+30 MW),
      // dataCenter (+10 MW) and the cooling network's ε-NTU HX coupling were
      // silent no-ops. Convert it to a per-kg enthalpy change at apply time:
      //     Δh_Q = Q̇ / ṁ_outflow          [J/kg]
      // distributed over the junction's total outflow so multi-outlet
      // components conserve energy exactly (Σ ṁ_i·Δh_Q = Q̇). Work machines
      // (pump/compressor/turbine) are EXCLUDED: their energy transfer already
      // rides on deltaH, and consuming their shaft-work telemetry would
      // double-count (see the ComponentUpdate contract in types.ts).
      let deltaH_fromQ = 0;
      const isHeatSource =
        comp.type === 'boiler' || comp.type === 'heatExchanger' || comp.type === 'custom';
      // Builder topologies can give one component TWO junctions (an inlet-side
      // junction with only inflow connections and an outlet-side junction with
      // the outflow). The duty is carried — and _appliedQ_W recorded — ONLY at
      // outflow-bearing junctions, so the inlet-side pass can never clobber
      // the applied-duty bookkeeping (Map iteration order must not matter).
      let hasOutflow = false;
      for (const conn of junction.connections) {
        if (conn.direction === 'outflow' && pipes.has(conn.pipeId)) { hasOutflow = true; break; }
      }
      if (isHeatSource && hasOutflow && update.energySource !== 0) {
        let mdotOutflow = 0;
        for (const conn of junction.connections) {
          if (conn.direction !== 'outflow') continue;
          const pipe = pipes.get(conn.pipeId);
          if (!pipe) continue;
          mdotOutflow += Math.abs(pipe.faces[conn.faceIndex].mdot);
        }
        // CARRY GUARD: the throughflow must be able to carry the duty within
        // a physically meaningful enthalpy change — ṁ ≥ |Q̇| / Δh_max. Below
        // that (startup trickle flows against a utility-scale duty) Q̇/ṁ
        // produces astronomic Δh that rails the h clamp and poisons the loop
        // state (probe-verified: a 7.5 MW rack duty at ~1e-3 kg/s startup flow
        // wrote 5e6-J/kg cells → ~1140 °C phantom temperatures and runaway
        // low-density velocities). Physically: a device whose stream cannot
        // absorb its duty has no valid steady state — we apply ZERO duty and
        // report it honestly via _appliedQ_W = 0 until the flow develops.
        const mdotMinCarry = Math.abs(update.energySource) / MAX_CARRY_DH_JKG;
        if (mdotOutflow > Math.max(1e-6, mdotMinCarry) && isFinite(mdotOutflow)) {
          deltaH_fromQ = update.energySource / mdotOutflow;
        }
      }

      // Applied-duty bookkeeping (display truth): stateSync must report what
      // the solve APPLIED, not the configured parameter. Accumulated below
      // from the post-clamp enthalpies actually written to the outlet cells.
      let appliedQ_W = 0;

      // Component modifies the OUTLET pipe's boundary cell
      for (const conn of junction.connections) {
        if (conn.direction !== 'outflow') continue;
        const pipe = pipes.get(conn.pipeId);
        if (!pipe) continue;

        const cellIdx = conn.faceIndex === 0 ? 0 : pipe.nCells - 1;

        // Apply pressure change (e.g., pump adds ΔP, turbine removes ΔP)
        if (update.deltaP !== 0) {
          pipe.cells[cellIdx].P = junction.P + update.deltaP;
          // Clamp to physical range
          pipe.cells[cellIdx].P = Math.max(611, Math.min(100e6, pipe.cells[cellIdx].P));
        }

        // Apply enthalpy: h_out = junction.h + deltaH_total.
        //
        // R2-B3: this write is UNCONDITIONAL — it fires even when
        // deltaH_total === 0. A zero-Δh component (valve / control-valve /
        // check-valve / relief / pressure-regulator / adiabatic HX) is an
        // ISENTHALPIC pass-through, so its outlet cell must still
        // republish the junction's mass-weighted mixed enthalpy (h_out =
        // junction.h) downstream. The old `deltaH_total !== 0` gate left the
        // downstream inlet cell pinned to its INITIAL enthalpy, freezing a
        // thermal front crossing these passive junctions: the energy solver's
        // inflow-boundary term then advects the cell's own (never-refreshed)
        // old value instead of the upstream mixed state. Active components
        // (deltaH_total !== 0) are numerically byte-identical to before — the
        // written value junction.h + deltaH_total is unchanged.
        const deltaH_total = update.deltaH + deltaH_fromQ;
        pipe.cells[cellIdx].h = Math.max(100, Math.min(5e6, junction.h + deltaH_total));
        // Applied Q share on this outlet = ṁ_out × (h_written − (h_junction + deltaH)).
        // Equals the requested share exactly unless the h clamp engaged.
        if (deltaH_fromQ !== 0) {
          appliedQ_W += Math.abs(pipe.faces[conn.faceIndex].mdot) *
            (pipe.cells[cellIdx].h - (junction.h + update.deltaH));
        }
        // Re-update properties for this cell from its (P, h).
        try {
          const props = propsFromPH_SI(pipe.cells[cellIdx].P, pipe.cells[cellIdx].h);
          pipe.cells[cellIdx].rho = props.rho;
          pipe.cells[cellIdx].T = props.T;
          pipe.cells[cellIdx].c = props.c;
          pipe.cells[cellIdx].mu = props.mu;
        } catch { /* keep existing */ }

        // Enforce mass flow if component specifies it
        if (update.mdot != null) {
          pipe.faces[conn.faceIndex].mdot = update.mdot;
          const rho = faceRho(pipe, conn.faceIndex);
          pipe.faces[conn.faceIndex].u = rho > 0 ? update.mdot / (rho * pipe.area) : 0;
        }
      }

      // N2: a HARD BLOCK (mdot === 0, e.g. a closed check valve or a stopped
      // positive-displacement pump) stagnates BOTH ports, so the inflow faces
      // are pinned to zero as well. Constraining only the outflow face left
      // the upstream pipe's free boundary siphoning mass into a dead-ended
      // junction forever (permanent junction imbalance — mass vanishing,
      // CLAUDE.md rule 3). Positive mdot constraints (pumps delivering flow)
      // are untouched: their suction side must remain free to develop.
      if (update.mdot === 0) {
        for (const conn of junction.connections) {
          if (conn.direction !== 'inflow') continue;
          const pipe = pipes.get(conn.pipeId);
          if (!pipe) continue;
          pipe.faces[conn.faceIndex].mdot = 0;
          pipe.faces[conn.faceIndex].u = 0;
        }
      }

      // Mirror the applied duty onto the component for the UI sync
      // (read-only side channel; never read by the solve). 0 = duty configured
      // but not carried (no throughflow) — the honest display value. Written
      // ONLY at outflow-bearing junctions (see hasOutflow above).
      if (isHeatSource && hasOutflow) {
        comp.params._appliedQ_W = update.energySource !== 0 ? appliedQ_W : 0;
      }
    }

    // --- Step 4b: Isenthalpic advection across PASSIVE (component-less) junctions ---
    //
    // R2-B3. A non-boundary junction with NO resolvable component — a plain
    // 'merge' / 'split' / header topology node such as an open-feedwater-heater
    // mixing point — is a zero-volume adiabatic node: the fluid leaving it
    // carries the mass-weighted mix of everything flowing IN. Step 4 skips these
    // (`!junction.componentId`), and Step 3 publishes the mixed enthalpy onto the
    // junction NODE only — it is never written to the OUTFLOW pipe cells. The
    // downstream inlet cell is therefore pinned to its initial enthalpy and a
    // thermal front cannot cross the junction (masked in shipped nets solely
    // because their passive junctions were initialised at the correct mix).
    //
    // FIX: compute the inflow-mass-weighted mixed enthalpy (the true adiabatic-
    // mixing outlet state) and Dirichlet-pin it onto every outflow boundary cell
    // — mirroring how boundary junctions pin in Step 1 and component junctions
    // pin in Step 4. This closes mixing energy EXACTLY (Σ ṁ_in·h_in = ṁ_out·h_mix,
    // since mass conservation gives Σ ṁ_out = Σ ṁ_in) and reduces to isenthalpic
    // pass-through (h_out = h_in) for a single-inlet header. Weighting mirrors the
    // Step-3 inflow-only convention so a plain merge behaves thermally like a
    // component (mixer) junction. Pressure is untouched (Step 3 owns it); only h
    // and its derived (T, ρ, c, μ) are refreshed.
    for (const junction of junctions.values()) {
      if (junction.type === 'boundary') continue;
      if (junction.componentId && components.has(junction.componentId)) continue; // handled in Step 4

      let mdotIn = 0;
      let hFluxIn = 0;
      for (const conn of junction.connections) {
        if (conn.direction !== 'inflow') continue;
        const pipe = pipes.get(conn.pipeId);
        if (!pipe) continue;
        const cellIdx = conn.faceIndex === 0 ? 0 : pipe.nCells - 1;
        const mdot = Math.abs(pipe.faces[conn.faceIndex].mdot);
        mdotIn += mdot;
        hFluxIn += mdot * pipe.cells[cellIdx].h;
      }
      // No resolvable inflow (quiescent start, or an all-outflow node) → leave
      // the outflow cells untouched; there is no advected state to carry yet.
      if (!(mdotIn > 1e-10) || !isFinite(hFluxIn)) continue;

      const h_mix = Math.max(100, Math.min(5e6, hFluxIn / mdotIn));
      junction.h = h_mix; // publish the true mixed enthalpy on the node

      for (const conn of junction.connections) {
        if (conn.direction !== 'outflow') continue;
        const pipe = pipes.get(conn.pipeId);
        if (!pipe) continue;
        const cellIdx = conn.faceIndex === 0 ? 0 : pipe.nCells - 1;
        pipe.cells[cellIdx].h = h_mix;
        try {
          const props = propsFromPH_SI(pipe.cells[cellIdx].P, pipe.cells[cellIdx].h);
          pipe.cells[cellIdx].rho = props.rho;
          pipe.cells[cellIdx].T = props.T;
          pipe.cells[cellIdx].c = props.c;
          pipe.cells[cellIdx].mu = props.mu;
        } catch { /* keep existing */ }
      }
    }

    // --- Step 5: Check convergence ---
    momentumResidual = 0;
    for (const junction of junctions.values()) {
      if (junction.type === 'boundary') continue;
      let netMdot = 0;
      for (const conn of junction.connections) {
        const pipe = pipes.get(conn.pipeId);
        if (!pipe) continue;
        const mdot = pipe.faces[conn.faceIndex].mdot;
        netMdot += conn.direction === 'inflow' ? mdot : -mdot;
      }
      momentumResidual = Math.max(momentumResidual, Math.abs(netMdot));
    }

    state.iteration++;

    // Early exit on NaN/divergence — prevents wasting iterations on a blown-up solution
    if (!isFinite(massResidual) || !isFinite(energyResidual) || !isFinite(momentumResidual)) {
      break;
    }

    if (massResidual < config.convergenceTolerance &&
        energyResidual < config.convergenceTolerance * 1000 &&
        momentumResidual < config.convergenceTolerance * 10) {
      converged = true;
      break;
    }

    // Adaptive relaxation: if residuals are decreasing, cautiously increase
    // relaxation for faster convergence. If increasing, tighten for stability.
    if (iter > 0 && isFinite(massResidual) && isFinite(prevMassResidual) && prevMassResidual > 0) {
      const ratio = massResidual / prevMassResidual;
      if (ratio < 0.8) {
        // Converging well — slightly increase relaxation (up to initial values)
        alpha_P = Math.min(config.pressureRelaxation, alpha_P * 1.05);
        alpha_u = Math.min(config.velocityRelaxation, alpha_u * 1.05);
      } else if (ratio > 1.2) {
        // Diverging — reduce relaxation (down to 50% of initial)
        alpha_P = Math.max(config.pressureRelaxation * 0.5, alpha_P * 0.8);
        alpha_u = Math.max(config.velocityRelaxation * 0.5, alpha_u * 0.8);
      }
    }
    prevMassResidual = massResidual;
  }

  state.converged = converged;
  // Surface divergence: a blown-up solve (non-finite residual, early-exit above)
  // must NOT report residual 0 — that masked divergence as a perpetual amber
  // "Solving…". Report POSITIVE_INFINITY so downstream convergence checks (and
  // the "Solver diverging" guidance) fire instead of treating it as converged.
  state.residuals = {
    mass: isFinite(massResidual) ? massResidual : Number.POSITIVE_INFINITY,
    momentum: isFinite(momentumResidual) ? momentumResidual : Number.POSITIVE_INFINITY,
    energy: isFinite(energyResidual) ? energyResidual : Number.POSITIVE_INFINITY,
  };

  // N6: aggregate the per-pipe two-phase friction-cap flags into a per-step
  // network-level diagnostic (read-only; surfaced via simState.warnings).
  let capEngaged = false;
  for (const pipe of pipes.values()) {
    if (pipe.twoPhaseFrictionCapEngaged) { capEngaged = true; break; }
  }
  state.twoPhaseFrictionCapEngaged = capEngaged;

  // Compute energy balance diagnostic (1st law check)
  let totalU = 0;
  let totalKE = 0;
  for (const pipe of pipes.values()) {
    const V = pipe.area * pipe.dx;
    for (let i = 0; i < pipe.nCells; i++) {
      const cell = pipe.cells[i];
      totalU += cell.rho * cell.u_int * V;
      const u_avg = 0.5 * (Math.abs(pipe.faces[i].u) + Math.abs(pipe.faces[i + 1].u));
      totalKE += 0.5 * cell.rho * u_avg * u_avg * V;
    }
  }
  const prevTotal = state.energyBalance?.totalInternalEnergy ?? totalU;
  const totalNow = totalU + totalKE;
  const prevNow = prevTotal + (state.energyBalance?.totalKineticEnergy ?? totalKE);
  state.energyBalance = {
    totalInternalEnergy: totalU,
    totalKineticEnergy: totalKE,
    imbalance: prevNow > 0 ? Math.abs(totalNow - prevNow) / prevNow : 0,
  };

  state.time += dt;
}

/**
 * Get fixed pressure for a pipe face — ONLY from 'boundary' type junctions.
 * Component junctions are FREE — their pressure is solved by SIMPLE.
 */
function getFixedPressure(
  pipe: PipeSegment, faceIndex: number, junctions: Map<string, Junction>
): number | null {
  for (const junction of junctions.values()) {
    if (junction.type !== 'boundary') continue;
    for (const conn of junction.connections) {
      if (conn.pipeId === pipe.id && conn.faceIndex === faceIndex) {
        return junction.P;
      }
    }
  }
  return null;
}

/**
 * Threshold (percent) above which a first-law closure deviation is flagged.
 * Per CLAUDE.md conservation-law rule: "Flag deviations above 0.1%."
 */
export const ENERGY_CLOSURE_WARN_PCT = 0.1;

/**
 * User-facing warning emitted when the two-phase friction cap engaged (N6).
 * Shared by stepAdapter (main thread) and solverWorker so both paths surface
 * the IDENTICAL text (worker/main parity).
 */
export const TWO_PHASE_CAP_WARNING =
  'Two-phase friction multiplier hit its 50x stability cap — wet-line pressure drop is under-predicted (low-pressure two-phase regime).';

export interface EnergyClosure {
  /**
   * First-law (energy) closure across the network, in percent.
   *
   * READ-ONLY derivation of the solver's already-computed `state.energyBalance`:
   * the fractional net energy imbalance (change in total stored internal +
   * kinetic energy relative to the previous step's total throughput) expressed
   * as a percentage. At steady-state convergence the stored energy is stationary
   * so this approaches 0%. `null` when no energy-balance diagnostic exists yet.
   */
  closurePct: number | null;
  /** Warning string when |closurePct| exceeds ENERGY_CLOSURE_WARN_PCT, else null. */
  warning: string | null;
}

/**
 * Derive a first-law closure diagnostic from the NS solver's already-computed
 * energy balance. This is a pure READ — it does NOT run the solver, mutate
 * state, change convergence criteria, or alter any thermodynamic formula.
 *
 * The underlying `state.energyBalance.imbalance` is the fractional change in
 * total stored energy (internal + kinetic) between consecutive steps, normalized
 * by the previous step's total. That ratio is the network's per-step energy
 * conservation residual relative to throughput; at convergence it tends to 0.
 *
 * @param state - solver state carrying the energy-balance diagnostic
 * @returns closure percentage and an optional >0.1% warning string
 */
export function computeEnergyClosure(state: NSSolverState): EnergyClosure {
  const balance = state.energyBalance;
  if (!balance || !isFinite(balance.imbalance)) {
    return { closurePct: null, warning: null };
  }

  // imbalance is a dimensionless fraction (ΔE_total / E_total_prev) → percent.
  const closurePct = Math.abs(balance.imbalance) * 100;

  const warning = closurePct > ENERGY_CLOSURE_WARN_PCT
    ? `First-law energy closure ${closurePct.toFixed(3)}% exceeds ${ENERGY_CLOSURE_WARN_PCT}% tolerance — network energy not conserved within tolerance.`
    : null;

  return { closurePct, warning };
}

export function networkMaxTimeStep(state: NSSolverState, CFL_max: number = 5): number {
  let dt_min = Infinity;
  for (const pipe of state.pipes.values()) {
    for (let i = 0; i < pipe.nCells; i++) {
      const u = Math.abs(pipe.faces[i].u);
      const c = pipe.cells[i].c;
      const dt = pipe.dx / (u + c + 1e-10);
      if (dt < dt_min) dt_min = dt;
    }
  }
  return dt_min * CFL_max;
}

export function createNSSolverState(
  pipes: Map<string, PipeSegment>,
  junctions: Map<string, Junction>,
  components: Map<string, NSComponent>,
  config: SIMPLEConfig = DEFAULT_SIMPLE_CONFIG,
): NSSolverState {
  return {
    pipes, junctions, components, config,
    time: 0, iteration: 0, converged: false,
    residuals: { mass: Infinity, momentum: Infinity, energy: Infinity },
  };
}

/**
 * Bounds for user-configurable convergence tolerance (the mass-residual
 * threshold). A tolerance above 1e-1 is meaninglessly loose; below 1e-8 it can
 * never be met in floating point and the solve would spin to maxOuterIterations
 * every step. Out-of-range / non-finite values fall back to the base config.
 */
export const CONVERGENCE_TOLERANCE_MIN = 1e-8;
export const CONVERGENCE_TOLERANCE_MAX = 1e-1;

/**
 * Neutral reference for the single user-facing relaxation knob.
 *
 * The toolbar exposes ONE relaxation factor (range 0.05–1.0, UI default 0.8),
 * but the SIMPLE solver carries THREE under-relaxation coefficients
 * (pressure/velocity/enthalpy), each tuned per network (auto-built 0.3/0.7/0.8,
 * BoP closed loops 0.05/0.1/0.3). We therefore apply the user knob as a
 * MULTIPLICATIVE SCALE on whatever the network's base triple is, normalized so
 * that the UI default reproduces the base config EXACTLY:
 *
 *   scale = relaxationFactor / RELAXATION_REFERENCE
 *
 * At relaxationFactor = 0.8 (default/unset) the scale is 1.0, so 0.3/0.7/0.8
 * (and the BoP-tuned 0.05/0.1/0.3) are preserved bit-for-bit. Lowering the knob
 * relaxes more conservatively (more stable); raising it relaxes harder, with
 * each scaled coefficient clamped to (0, 1].
 */
export const RELAXATION_REFERENCE = 0.8;

/** Smallest meaningful relaxation coefficient (matches the toolbar slider min). */
export const RELAXATION_MIN = 0.05;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Derive the effective SIMPLE config by layering the user's SIMULATION SETTINGS
 * over a network's base config — WITHOUT touching any solve math.
 *
 * This is the single seam that makes the toolbar's relaxation / tolerance knobs
 * actually reach the solver. It ONLY reads user values into the config; it does
 * not change the SIMPLE loop, convergence criteria, or any thermodynamic
 * formula. Every override is clamped to a physically sane range, and any
 * unset / non-finite / out-of-range value falls back to the base config so the
 * existing per-network defaults (0.3/0.7/0.8 auto-built, 0.05/0.1/0.3 BoP) are
 * preserved exactly.
 *
 * @param base    The network's base SIMPLE config (factory- or builder-chosen).
 * @param settings User-configured relaxation / tolerance (from SimulationState).
 *                 Both fields optional; missing/invalid → base values kept.
 */
export function deriveSolverConfig(
  base: SIMPLEConfig,
  settings: { relaxationFactor?: number; convergenceTolerance?: number; enableAcoustic?: boolean },
): SIMPLEConfig {
  const next: SIMPLEConfig = { ...base };

  // --- W7 P3: MOC water-hammer overlay toggle (default stays OFF). ---
  if (typeof settings.enableAcoustic === 'boolean') {
    next.enableAcoustic = settings.enableAcoustic;
  }

  // --- Convergence tolerance: direct override, clamped to a sane range. ---
  const tol = settings.convergenceTolerance;
  if (typeof tol === 'number' && isFinite(tol) && tol > 0) {
    next.convergenceTolerance = clamp(tol, CONVERGENCE_TOLERANCE_MIN, CONVERGENCE_TOLERANCE_MAX);
  }
  // else: keep base.convergenceTolerance (default 1e-4).

  // --- Relaxation: single knob applied as a normalized scale on the triple. ---
  const relax = settings.relaxationFactor;
  if (typeof relax === 'number' && isFinite(relax) && relax > 0) {
    // Clamp the knob itself to (0, 1] before scaling.
    const knob = clamp(relax, RELAXATION_MIN, 1.0);
    const scale = knob / RELAXATION_REFERENCE;
    // Each scaled coefficient stays in (0, 1]; scale=1 (knob=0.8) is a no-op.
    next.pressureRelaxation = clamp(base.pressureRelaxation * scale, 1e-4, 1.0);
    next.velocityRelaxation = clamp(base.velocityRelaxation * scale, 1e-4, 1.0);
    next.enthalpyRelaxation = clamp(base.enthalpyRelaxation * scale, 1e-4, 1.0);
  }
  // else: keep base relaxation triple (0.3/0.7/0.8 or BoP 0.05/0.1/0.3).

  return next;
}
